// Type-safe wrapper for Google Analytics
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Onboarding funnel event names for tracking user journey
 */
export type OnboardingEvent =
  | 'signup_complete'
  | 'email_verified'
  | 'mobile_verified'
  | 'shipping_address_added'
  | 'first_trade_outbound'
  | 'first_trade_inbound';

/**
 * Get referral source from localStorage (stored from URL params on landing)
 * Supports both ?ref=partner and ?utm_source=partner URLs
 */
const getReferralSource = (): string | undefined => {
  try {
    return localStorage.getItem('referral_source') || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Get standard user properties for GA4 events
 * @param user - Object containing user id, createdAt, and country
 * @returns Object with user_id, signup_date, country, and referral_source
 */
export const getUserProperties = (user: {
  id?: number;
  createdAt?: string;
  country?: string | null;
}) => ({
  user_id: user.id?.toString(),
  signup_date: user.createdAt ? user.createdAt.split('T')[0] : undefined,
  country: user.country || undefined,
  referral_source: getReferralSource(),
});

/**
 * Track a page view in Google Analytics
 * @param url - The full URL path including search params (e.g., "/my-cards?tab=want-list")
 */
export const trackPageView = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-NKKX593FTB', {
      page_path: url,
    });
  }
};

/**
 * Track a custom event in Google Analytics
 * @param eventName - Name of the event (e.g., "trade_completed", "card_added")
 * @param eventParams - Optional parameters for the event
 *
 * @example
 * trackEvent('trade_completed', {
 *   trade_value: 50.00,
 *   card_count: 5
 * });
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Google Ads "Sign ups" conversion action (AW-18345435010, label 98b5CN7SydUcEIK_5KtE).
 * The action's data source is "Manual event", so it records nothing until this
 * event is sent. Keep as a named constant — no inline literals at call sites.
 */
export const GOOGLE_ADS_SIGNUP_CONVERSION = 'AW-18345435010/98b5CN7SydUcEIK_5KtE';

/** Fire-once-per-session guard for the signup conversion. */
const SIGNUP_CONVERSION_SESSION_KEY = 'tm_signup_conv';

/**
 * Report a completed account creation to Google Ads.
 *
 * Call ONLY from the account-creation success path, after the signup API has
 * confirmed the account exists — never on button click, on a post-signup route
 * mount, or anywhere login can reach. It is deliberately tied to the same event
 * the admin dashboard counts as a new user (a `User` row being created by
 * POST /auth/beta-register), so the two figures stay reconcilable.
 *
 * Never throws: a missed conversion is far better than a broken signup.
 *
 * @param email - The account's email. Sent via gtag `user_data` for enhanced
 *   conversions; gtag hashes it client-side, so it must NOT be hashed here.
 */
export const trackSignupConversion = (email?: string): void => {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;

  try {
    if (sessionStorage.getItem(SIGNUP_CONVERSION_SESSION_KEY)) return;
    sessionStorage.setItem(SIGNUP_CONVERSION_SESSION_KEY, '1');
  } catch {
    // Private-browsing modes throw on sessionStorage access. Fire anyway —
    // a duplicate is preferable to silently dropping the conversion.
  }

  if (email) {
    window.gtag('set', 'user_data', { email: email.trim().toLowerCase() });
  }

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_SIGNUP_CONVERSION,
    value: 1.0,
    currency: 'USD',
  });
};
